import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import {
  ASSISTANT_SUGGESTIONS,
  type AssistantRunRequest,
  type AssistantStreamEvent,
  type AssistantToolCallView,
  type AssistantTurnView,
} from '@fieldops/shared'
import { runAssistant } from '../../lib/assistant-stream.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { EmailCompose, type ComposeDraft } from '../email/EmailCompose.js'
import { Icon } from '../Icon.js'
import { Spinner } from '../ui.js'
import { ActionCard } from './assistant/ActionCard.js'
import { Markdown } from './assistant/Markdown.js'
import { emailContacts, primaryPerson } from './panes.js'
import { SmsCompose } from './SmsCompose.js'
import type { Job } from './types.js'

/**
 * The job card's Assistant tab: a conversation about this job that can act
 * on it.
 *
 * It resumes this person's latest conversation on the job, or starts one
 * with a "Where this job is" summary. What the assistant does arrives as
 * cards (`assistant/ActionCard.tsx`): reads as steps, notes and people as
 * receipts with Undo, and anything that books, prices or reaches a customer
 * as a card that waits for Approve. SMS still leaves from the phone, through
 * `SmsCompose`, and Edit in composer is `EmailCompose` (rule 45). The words
 * stream over `POST /assistant/run`; everything else is tRPC. Decision 94.
 */
export function AssistantPane({ job }: { job: Job }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const sessions = useQuery(trpc.assistant.sessions.queryOptions({ jobId: job.id }))
  const [sessionId, setSessionId] = useState<string | null>(null)
  const session = useQuery({
    ...trpc.assistant.session.queryOptions({ id: sessionId ?? '' }),
    enabled: sessionId !== null,
  })

  const [turns, setTurns] = useState<AssistantTurnView[]>([])
  const [streaming, setStreaming] = useState('')
  const [running, setRunning] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [email, setEmail] = useState<{ call: AssistantToolCallView; draft: ComposeDraft } | null>(null)
  const [sms, setSms] = useState<{ to: string | null; body: string } | null>(null)

  const live = sessions.data?.live ?? false
  const startedFor = useRef<string | null>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const abort = useRef<AbortController | null>(null)

  const start = useMutation(trpc.assistant.start.mutationOptions())
  const undo = useMutation(
    trpc.assistant.undo.mutationOptions({
      onSuccess: (call) => {
        replaceCall(call)
        refreshJob()
      },
      onError: (error) => setProblem(errorMessage(error)),
    }),
  )

  const refreshJob = () => {
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
    void queryClient.invalidateQueries(trpc.inbox.pathFilter())
  }

  const replaceCall = (call: AssistantToolCallView) =>
    setTurns((current) => {
      const index = current.findIndex((turn) => turn.id === call.turnId)
      const at = index === -1 ? current.length - 1 : index
      if (at < 0) return current
      return current.map((turn, position) => {
        if (position !== at) return turn
        const exists = turn.toolCalls.some((row) => row.id === call.id)
        return {
          ...turn,
          toolCalls: exists ? turn.toolCalls.map((row) => (row.id === call.id ? call : row)) : [...turn.toolCalls, call],
        }
      })
    })

  const onEvent = (event: AssistantStreamEvent) => {
    switch (event.type) {
      case 'turn':
        setTurns((current) =>
          current.some((turn) => turn.id === event.turn.id)
            ? current.map((turn) => (turn.id === event.turn.id ? event.turn : turn))
            : [...current, event.turn],
        )
        if (event.turn.role === 'ASSISTANT') setStreaming('')
        break
      case 'text':
        setStreaming((current) => current + event.delta)
        break
      case 'toolCall':
        replaceCall(event.toolCall)
        break
      case 'done':
        setTurns(event.session.turns)
        void queryClient.invalidateQueries(trpc.assistant.pathFilter())
        break
      case 'error':
        setProblem(event.message)
        break
    }
  }

  const run = async (request: AssistantRunRequest) => {
    abort.current?.abort()
    const controller = new AbortController()
    abort.current = controller
    setRunning(true)
    setProblem(null)
    setStreaming('')
    try {
      await runAssistant(request, onEvent, controller.signal)
    } catch (error) {
      if (!controller.signal.aborted) setProblem(error instanceof Error ? error.message : 'The assistant did not answer.')
    } finally {
      if (abort.current === controller) {
        setRunning(false)
        setStreaming('')
        refreshJob()
      }
    }
  }

  const begin = async () => {
    const created = await start.mutateAsync({ jobId: job.id })
    setSessionId(created.id)
    setTurns([])
    void queryClient.invalidateQueries(trpc.assistant.sessions.pathFilter())
    await run({ sessionId: created.id, action: 'summary' })
  }

  // Resume the latest conversation on this job, or open a new one with the
  // summary. Once per job: StrictMode's second effect and a refetch of the
  // list must not start a second conversation.
  useEffect(() => {
    if (!sessions.data || startedFor.current === job.id) return
    startedFor.current = job.id
    const latest = sessions.data.sessions[0]
    if (latest) setSessionId(latest.id)
    else if (sessions.data.live) void begin().catch((error: unknown) => setProblem(errorMessage(error)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions.data, job.id])

  // The saved conversation replaces what the stream drew, except mid-run.
  useEffect(() => {
    if (session.data && !running) setTurns(session.data.turns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.data])

  useEffect(() => () => abort.current?.abort(), [])

  useEffect(() => {
    const node = scroller.current
    if (node) node.scrollTop = node.scrollHeight
  }, [turns, streaming, running])

  const send = (words: string) => {
    const message = words.trim()
    if (!message || !sessionId || running) return
    setText('')
    void run({ sessionId, action: 'message', text: message })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    send(text)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      send(text)
    }
  }

  const approve = (call: AssistantToolCallView) => {
    if (!sessionId) return
    if (call.card?.sms) setSms(call.card.sms)
    void run({ sessionId, action: 'approve', toolCallId: call.id })
  }

  const reject = (call: AssistantToolCallView, reason?: string, resume?: boolean) => {
    if (!sessionId) return
    void run({ sessionId, action: 'reject', toolCallId: call.id, reason, resume })
  }

  const person = primaryPerson(job)
  const firstUser = turns.findIndex((turn) => turn.role === 'USER')
  const canTalk = live && sessionId !== null && !running

  return (
    <section aria-label="Assistant" className="flex min-h-0 flex-1 flex-col bg-violet-50/40">
      <div
        ref={scroller}
        role="log"
        aria-label="Conversation"
        className="min-h-0 flex-1 overflow-y-auto px-[22px] py-5"
      >
        <div className="mx-auto flex max-w-[880px] flex-col gap-4">
          {sessions.data && !live ? (
            <div className="rounded-lg border border-ink-200 bg-ink-50 px-4 py-3 text-[13px] text-ink-700">
              <p className="font-semibold text-ink-900">The assistant is not connected here.</p>
              <p>It needs an OpenAI key on the server. Everything else on the job card works as usual.</p>
            </div>
          ) : null}

          {turns.map((turn, index) =>
            turn.role === 'USER' ? (
              <UserBubble key={turn.id} text={turn.content} />
            ) : (
              <AssistantTurn
                key={turn.id}
                turn={turn}
                summary={index === 0 && (firstUser === -1 || firstUser > 0) && turn.toolCalls.length === 0}
              >
                {turn.toolCalls.length === 0 ? null : turn.toolCalls.map((call) => (
                  <ActionCard
                    key={call.id}
                    call={call}
                    busy={running}
                    undoing={undo.isPending && undo.variables.toolCallId === call.id}
                    onApprove={() => approve(call)}
                    onReject={(reason, resume) => reject(call, reason, resume)}
                    onEditInComposer={
                      call.card?.email ? () => setEmail({ call, draft: call.card!.email! }) : undefined
                    }
                    onUndo={() => undo.mutate({ toolCallId: call.id })}
                  />
                ))}
              </AssistantTurn>
            ),
          )}

          {streaming ? <AssistantTurn turn={null} text={streaming} summary={turns.length === 0} /> : null}

          {running && !streaming ? (
            <p className="flex items-center gap-2 pl-10 text-[12.5px] text-ink-500" role="status">
              <Spinner className="size-3.5" /> Working on it…
            </p>
          ) : null}

          {problem ? (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-red-700">
              {problem}
            </p>
          ) : null}
        </div>
      </div>

      <div className="sticky bottom-0 shrink-0 px-[22px] pt-2 pb-4">
        <div className="mx-auto flex max-w-[880px] flex-col gap-2.5">
          <div className="flex items-center gap-1.5">
            <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-0.5" role="group" aria-label="Suggestions">
              {ASSISTANT_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion.label}
                  type="button"
                  disabled={!canTalk}
                  onClick={() => send(suggestion.prompt)}
                  className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[12px] font-medium whitespace-nowrap text-violet-700 ring-1 ring-violet-200 ring-inset hover:bg-violet-50 disabled:opacity-50"
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={!live || running || start.isPending}
              onClick={() => void begin().catch((error: unknown) => setProblem(errorMessage(error)))}
              className="flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[12px] font-medium text-ink-500 hover:text-violet-700 disabled:opacity-50"
            >
              <Icon name="plus" className="size-3.5" />
              New chat
            </button>
          </div>

          <form
            onSubmit={submit}
            className="flex items-end gap-2 rounded-xl bg-white py-2 pr-2 pl-3.5 shadow-[0_1px_2px_rgb(76_29_149/0.06)] ring-1 ring-violet-300 ring-inset focus-within:ring-2 focus-within:ring-violet-500/60"
          >
            <textarea
              aria-label="Message the assistant"
              rows={1}
              value={text}
              disabled={!live || sessionId === null}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about this job, or tell the assistant what to write…"
              className="max-h-40 min-h-[32px] flex-1 resize-none bg-transparent py-1.5 text-[13.5px] text-ink-900 outline-none field-sizing-content placeholder:text-ink-400"
            />
            <button
              type="submit"
              aria-label="Send"
              disabled={!canTalk}
              className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
            >
              <Icon name="send" className="size-4" />
            </button>
          </form>
        </div>
      </div>

      <EmailCompose
        open={email !== null}
        onClose={() => setEmail(null)}
        jobId={job.id}
        clientId={job.client.id}
        contacts={emailContacts(job)}
        files={job.attachments.map((file) => ({ id: file.id, name: file.filename }))}
        initial={email?.draft}
        onSent={() => {
          if (email) {
            reject(email.call, 'Edited and sent from the email window instead.', false)
          }
        }}
      />
      <SmsCompose
        open={sms !== null}
        onClose={() => setSms(null)}
        contactName={person?.name ?? job.client.name}
        phone={sms?.to ?? null}
        initialBody={sms?.body}
        jobId={job.id}
      />
    </section>
  )
}

function Avatar() {
  return (
    <span
      aria-hidden="true"
      className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-violet-500 to-violet-700 text-white shadow-sm"
    >
      <Icon name="sparkles" className="size-4" />
    </span>
  )
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <p className="max-w-[80%] rounded-xl bg-slate-100 px-3.5 py-2 text-[13.5px] whitespace-pre-wrap text-ink-900">
        {text}
      </p>
    </div>
  )
}

function AssistantTurn({
  turn,
  text,
  summary,
  children,
}: {
  turn: AssistantTurnView | null
  text?: string
  summary: boolean
  children?: ReactNode
}) {
  const words = text ?? turn?.content ?? ''
  if (!words.trim() && !children) return null
  return (
    <div className="flex gap-3">
      <Avatar />
      <div className="flex min-w-0 flex-1 flex-col gap-2.5 pt-1">
        {words.trim() ? (
          <Markdown
            text={words}
            lead={summary ? <strong className="font-semibold text-ink-900">Where this job is: </strong> : undefined}
          />
        ) : null}
        {children}
      </div>
    </div>
  )
}
